import "@/utils/i18n"

import { CssBaseline } from "@mui/material"
import { useEffect } from "react"
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom"

import { DesktopRoutes } from "@/App/Equities/desktop/DesktopRoutes"
import { DisconnectionOverlay } from "@/components/DisconnectionOverlay"
import { ROUTES_CONFIG } from "@/constants"
import { useIsDesktop } from "@/utils/useIsDesktop"

import { Login as DesktopLogin } from "./Equities/desktop/components/Login/Login"
import { DesktopMobileSwitch } from "./Equities/DesktopMobileSwitch"
import { MobileLogin } from "./Equities/mobile/components/login/MobileLogin"
import { MobileRoutes } from "./Equities/mobile/MobileRoutes"
import { EquitiesErrorAlert } from "./Equities/shared/components/errorAlert"
import {
  useAuthError,
  useLoggedInUser,
  useLoginSuccessCount,
} from "./Equities/shared/services/login"

export const EquitiesRoutes = () => {
  const isMockMode = !!import.meta.env.VITE_EQUITIES_MOCKS
  const loggedInUser = useLoggedInUser()
  const loginSuccessCount = useLoginSuccessCount()
  const authError = useAuthError()
  const isDesktop = useIsDesktop()
  const navigate = useNavigate()
  const location = useLocation()
  const isOnLoginPage = location.pathname.includes("login")
  const isAuthenticated = isMockMode || !!loggedInUser

  const isRestoringSession =
    !isMockMode &&
    !loggedInUser &&
    !authError && // without this, a failed auto-login would block <Routes> and never redirect
    !isOnLoginPage &&
    !!localStorage.getItem("email")

  useEffect(() => {
    if (loggedInUser && isOnLoginPage) {
      navigate(isDesktop ? "desktop" : "mobile", {
        replace: true,
      })
    }
  }, [isDesktop, isOnLoginPage, loggedInUser, loginSuccessCount, navigate])

  if (isRestoringSession) {
    return <EquitiesErrorAlert />
  }

  return (
    <>
      <EquitiesErrorAlert />
      <CssBaseline />
      <DesktopMobileSwitch />
      <DisconnectionOverlay />
      <Routes>
        {!isMockMode && (
          <>
            <Route
              path={ROUTES_CONFIG.equitiesDesktopLogin}
              element={<DesktopLogin />}
            />
            <Route
              path={ROUTES_CONFIG.equitiesMobileLogin}
              element={<MobileLogin />}
            />
          </>
        )}
        <Route
          path={ROUTES_CONFIG.equitiesDesktop}
          element={
            isAuthenticated ? (
              <DesktopRoutes />
            ) : (
              <Navigate to="login" replace />
            )
          }
        />
        <Route
          path={ROUTES_CONFIG.equitiesMobile}
          element={
            isAuthenticated ? <MobileRoutes /> : <Navigate to="login" replace />
          }
        />
        <Route
          path="/*"
          element={<Navigate to={isDesktop ? "desktop" : "mobile"} replace />}
        />
      </Routes>
    </>
  )
}
